import React, { useState } from "react";
import { ChevronDown, ChevronUp, Quote, Target, Lightbulb, ArrowDown } from "lucide-react";
import { findSpan, focusSpan } from "../../lib/evidence";

/**
 * ReflectiveDepthPanel — renders the deterministic reflective-practice
 * assessment produced by `services/reflective_assessor.py`. Audibleable:
 * every framework shows its depth level, mention count, and the
 * student-text evidence that drove that level.
 */

const LEVEL_META = {
  mentioned: { label: "Framework mentioned", colour: "#9CA3AF", pct: 25 },
  explained: { label: "Framework explained", colour: "#EA580C", pct: 50 },
  applied: { label: "Applied to a specific experience", colour: "#2563EB", pct: 75 },
  applied_with_change: { label: "Framework used to identify learning and future action", colour: "#16A34A", pct: 100 },
};

const LevelBar = ({ level }) => {
  const meta = LEVEL_META[level] || LEVEL_META.mentioned;
  return (
    <div className="flex items-center gap-2 min-w-[120px]">
      <div className="flex-1 h-1.5 bg-zinc-100 rounded-full overflow-hidden">
        <div className="h-full rounded-full transition-all" style={{ width: `${meta.pct}%`, backgroundColor: meta.colour }} />
      </div>
      <span className="text-[10px] uppercase tracking-wider font-bold tabular-nums" style={{ color: meta.colour }} data-testid={`reflective-level-${level}`}>
        {meta.pct}%
      </span>
    </div>
  );
};

const JumpLink = ({ spanId, children }) => {
  if (!spanId) return null;
  return (
    <button
      type="button"
      onClick={() => focusSpan(spanId)}
      className="inline-flex items-center gap-1 text-[11px] font-medium text-oxford hover:text-[#2C5282] underline-offset-2 hover:underline"
      data-testid={`reflective-jump-${spanId}`}
    >
      <ArrowDown size={11} /> {children}
    </button>
  );
};

const FrameworkRow = ({ fw, spans }) => {
  const [open, setOpen] = useState(false);
  const meta = LEVEL_META[fw.level] || LEVEL_META.mentioned;
  const hasEvidence = !!(fw.evidence || fw.learning_quote || fw.change_quote);

  const evidenceSpan = findSpan(spans, { source: "reflective_depth", source_key: fw.framework_id, kind: "evidence" });
  const learningSpan = findSpan(spans, { source: "reflective_depth", source_key: fw.framework_id, kind: "learning" });
  const changeSpan   = findSpan(spans, { source: "reflective_depth", source_key: fw.framework_id, kind: "change" });

  return (
    <div className="border-b border-border last:border-0 py-3" data-testid={`reflective-framework-${fw.framework_id}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <p className="text-sm font-medium text-foreground">{fw.display}</p>
            <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 text-white font-bold" style={{ backgroundColor: meta.colour }}>
              {fw.level.replace(/_/g, " ")}
            </span>
          </div>
          <p className="text-xs text-muted-foreground mt-0.5">
            {fw.level_label || meta.label} · {fw.mentions} mention{fw.mentions === 1 ? "" : "s"}
          </p>
        </div>
        <LevelBar level={fw.level} />
      </div>

      {hasEvidence && (
        <>
          <button
            type="button"
            onClick={() => setOpen((o) => !o)}
            className="mt-2 inline-flex items-center gap-1 text-[11px] font-medium text-oxford hover:text-[#2C5282]"
            data-testid={`reflective-toggle-${fw.framework_id}`}
            aria-expanded={open}
          >
            {open ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
            {open ? "Hide evidence" : "Show evidence from your writing"}
          </button>

          {open && (
            <div className="mt-2 space-y-3 bg-secondary border-l-2 border-oxford p-3 text-[13px] leading-relaxed" data-testid={`reflective-evidence-${fw.framework_id}`}>
              {fw.evidence && (
                <div className="flex gap-2">
                  <Quote size={13} className="text-muted-foreground mt-1 shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-foreground italic">&ldquo;{fw.evidence}&rdquo;</p>
                    {evidenceSpan && (
                      <div className="mt-1"><JumpLink spanId={evidenceSpan.id}>Jump to this in your document</JumpLink></div>
                    )}
                  </div>
                </div>
              )}
              {fw.learning_quote && (
                <div className="flex gap-2" data-testid={`reflective-learning-${fw.framework_id}`}>
                  <Target size={13} className="text-[#2563EB] mt-1 shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-bold">Specific experience / learning</p>
                    <p className="text-foreground italic">&ldquo;{fw.learning_quote}&rdquo;</p>
                    {learningSpan && (
                      <div className="mt-1"><JumpLink spanId={learningSpan.id}>Jump to this in your document</JumpLink></div>
                    )}
                  </div>
                </div>
              )}
              {fw.change_quote && (
                <div className="flex gap-2" data-testid={`reflective-change-${fw.framework_id}`}>
                  <Lightbulb size={13} className="text-[#16A34A] mt-1 shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-bold">Future action / change identified</p>
                    <p className="text-foreground italic">&ldquo;{fw.change_quote}&rdquo;</p>
                    {changeSpan && (
                      <div className="mt-1"><JumpLink spanId={changeSpan.id}>Jump to this in your document</JumpLink></div>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
};

export const ReflectiveDepthPanel = ({ assessment, spans = [] }) => {
  const score = assessment.score_out_of_5;
  const frameworks = assessment.per_framework || [];
  const topLevel = assessment.applied_level || "none";
  const topMeta = LEVEL_META[topLevel] || LEVEL_META.mentioned;

  return (
    <div className="mt-5 pt-5 border-t border-border" data-testid="reflective-depth-panel">
      <div className="flex items-start justify-between gap-3 mb-4">
        <div>
          <p className="overline text-muted-foreground">Reflective-practice depth</p>
          <p className="font-serif text-xl mt-0.5">
            {score != null ? `${score.toFixed(1)}/5` : "—"}
            <span className="text-xs text-muted-foreground ml-2">· top level: <span style={{ color: topMeta.colour }} className="font-medium">{topMeta.label}</span></span>
          </p>
        </div>
        <div className="text-right text-[11px] text-muted-foreground max-w-[200px]">
          <p>Deterministic score — explains how deeply you used each reflective framework, not just whether you named it.</p>
        </div>
      </div>

      <div className="bg-card border border-border">
        {frameworks.map((fw) => <FrameworkRow key={fw.framework_id} fw={fw} spans={spans} />)}
      </div>

      <p className="text-[10px] text-muted-foreground mt-3 leading-relaxed">
        <strong>How this is calculated:</strong> MyAE looks for each framework name
        (tolerating accents and author variants), then scans a sentence window around every
        mention. The level rises from <em>mentioned</em> → <em>explained</em> (stages/concepts
        described) → <em>applied</em> (a specific experience is linked) →
        <em> applied with change</em> (future action / learning identified). Scores are
        auditable from the student&apos;s own writing above — tap <em>Jump to this in your document</em> to see it in context.
      </p>
    </div>
  );
};

export default ReflectiveDepthPanel;
